import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerFooter,
  Button,
} from '@one-thd/sui-atomic-components';
import React, { useState } from 'react';
import { FeatureSwitches } from '../../../constants/feature-switches';
import { usePurchaseHistoryContext } from '../../../hooks/useContexts';
import { TDateSelectValues } from '../../../models/common';
import { DateRanges } from '../../../utils/DateRanges';
import {
  getEffectiveFilterDateRange,
  getFilterDetailsFromSession,
} from '../../../utils/purchase-history/common';
import {
  modifyDateFormat,
  parseDateToString,
  setPHFilterToSession,
} from '../../../utils/purchase-history/order-filter-utils';
import { OrderFilterDateSection } from './OrderFilterDateSection';
import { CustomDateDrawer } from './CustomDateDrawer';
import { DateFilterSettings, DateSettings } from '../../../models/order-filter';

interface OrderFilterDateDrawerProps {
  isDrawerOpen: boolean;
  setIsDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const OrderFilterDateDrawer = ({
  isDrawerOpen,
  setIsDrawerOpen,
}: OrderFilterDateDrawerProps) => {
  const { dateSelectorValue, pHRequest, setDateSelectorValue, setPHRequest } =
    usePurchaseHistoryContext();

  const sessionPHFilter = getFilterDetailsFromSession();

  const [filterSettings, setFilterSettings] = useState<DateFilterSettings>({
    customDateUsed: false,
    showStartCalendar: false,
    showEndCalendar: false,
    invalidStartDate: false,
    invalidEndDate: false,
  });

  const [dateSettings, setDateSettings] = useState<DateSettings>(() => {
    const { startDate, endDate } = getEffectiveFilterDateRange(
      pHRequest,
      sessionPHFilter,
    );
    const toMMDDYYYY = (date: string): string | null => {
      if (!date) return null;
      if (date.includes('/')) return date;
      return parseDateToString(date);
    };
    return {
      selectedDate: sessionPHFilter?.selectedDate ?? dateSelectorValue,
      startDate: toMMDDYYYY(startDate),
      endDate: toMMDDYYYY(endDate),
    };
  });

  const closeFilterDrawer = () => {
    setIsDrawerOpen(false);
  };

  const onDateSelect = (
    _event: React.SyntheticEvent<Element, Event>,
    date: string,
  ): void => {
    if (date === dateSettings.selectedDate) {
      return;
    }
    if (date) {
      setFilterSettings((prevState: DateFilterSettings) => ({
        ...prevState,
        customDateUsed: false,
        invalidStartDate: false,
        invalidEndDate: false,
      }));
      setDateSettings((prevState: DateSettings) => ({
        ...prevState,
        selectedDate: date as TDateSelectValues,
      }));
      const startDateStr =
        DateRanges?.[(date as keyof typeof DateRanges) ?? '']?.startDate || '';
      const endDateStr =
        DateRanges?.[(date as keyof typeof DateRanges) ?? '']?.endDate || '';
      const newStartDate = parseDateToString(startDateStr);
      const newEndDate = parseDateToString(endDateStr);
      setDateSettings((prevState: DateSettings) => ({
        ...prevState,
        startDate: startDateStr ? newStartDate : null,
        endDate: endDateStr ? newEndDate : null,
      }));
    }
  };

  const handleDateViewChange = () => {
    const [newStartDate, newEndDate] = modifyDateFormat(
      dateSettings.startDate,
      dateSettings.endDate,
    );

    setDateSelectorValue(
      (dateSettings.selectedDate as TDateSelectValues) ||
        (FeatureSwitches.enableLSSCPS25MonthsDefault
          ? ('last25Months' as TDateSelectValues)
          : ('last6Months' as TDateSelectValues)),
    );
    setPHRequest((prevState: typeof pHRequest) => {
      const nextStartDate = dateSettings.startDate ? newStartDate ?? '' : '';
      const nextEndDate = dateSettings.endDate ? newEndDate ?? '' : '';
      if (
        prevState.startDate === nextStartDate &&
        prevState.endDate === nextEndDate &&
        prevState.pageNumber === 1
      ) {
        return prevState;
      }

      return {
        ...prevState,
        startDate: nextStartDate,
        endDate: nextEndDate,
        pageNumber: 1,
      };
    });
    setPHFilterToSession({
      selectedDate: dateSettings.selectedDate,
      startDate: dateSettings.startDate ? newStartDate ?? '' : '',
      endDate: dateSettings.endDate ? newEndDate ?? '' : '',
    });
    closeFilterDrawer();
  };

  return (
    <>
      <Drawer
        open={isDrawerOpen}
        onClose={closeFilterDrawer}
        style={{ zIndex: 20000000010 }}
        data-testid="date-filter-drawer"
      >
        <DrawerHeader title="Date Placed" onClose={closeFilterDrawer} />
        <DrawerBody>
          <OrderFilterDateSection
            dateSettings={dateSettings}
            setDateSettings={setDateSettings}
            setFilterSettings={setFilterSettings}
            onDateSelect={onDateSelect}
          />
        </DrawerBody>
        <DrawerFooter>
          <Button
            variant="primary"
            fullWidth
            onClick={() => {
              const isCustom = dateSettings.selectedDate === 'custom';
              const startIncomplete =
                isCustom && dateSettings.startDate?.toString().length !== 10;
              const endIncomplete =
                isCustom && dateSettings.endDate?.toString().length !== 10;
              const hasError =
                !dateSettings.startDate ||
                !dateSettings.endDate ||
                filterSettings.invalidStartDate ||
                filterSettings.invalidEndDate ||
                startIncomplete ||
                endIncomplete;
              if (hasError) return;
              handleDateViewChange();
            }}
          >
            View Results
          </Button>
        </DrawerFooter>
      </Drawer>
      <CustomDateDrawer
        setFilterSettings={setFilterSettings}
        filterSettings={filterSettings}
        dateSettings={dateSettings}
        setDateSettings={setDateSettings}
      />
    </>
  );
};
